import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {Modal} from '@Components/modal';
import {closeModal, openPanelInModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {LazyPanel} from '@Components/lazy-panel';
import type {AiVideoPanelProps} from '@Panels/ai-video-panel/ai-video-panel.types';
import useWindowSize from '@Hooks/useWindowSize';
import {useAppDispatch, useAppSelector} from '@/hooks';

const PANEL_ID = 'ai-video-modal-panel';

export type AiVideoModalProps = Omit<AiVideoPanelProps, 'panelId' | 'isFullScreen'>;

export default function AiVideoModal(): ReactElement {
  const dispatch = useAppDispatch();
  const modalData = useAppSelector((state) => {
    return state.modals.modalsHashmap[MODALS_IDS.AI_VIDEO_MODAL];
  });
  const sizeData = useAppSelector((state) => {
    return state.panels.sizeDataHashmap[PANEL_ID];
  });
  const stickToBottom = useAppSelector((state) => {
    return state.panels.panelPropertiesHashmap[PANEL_ID]?.stickToBottom;
  });
  const {windowWidth} = useWindowSize();

  const callerProps = modalData.panelProps as AiVideoModalProps | undefined;

  const onClose = (): void => {
    dispatch(closeModal(MODALS_IDS.AI_VIDEO_MODAL));
    callerProps?.onClose?.();
  };

  const getInitialWidth = (): string => {
    if (windowWidth > 2000) {
      return '1600px';
    }
    if (windowWidth <= 2000 && windowWidth > 550) {
      return '80%';
    }
    return '600px';
  };

  const [height, setHeight] = useState('94%');
  const [width, setWidth] = useState(getInitialWidth());

  useEffect((): void => {
    if (sizeData?.height) {
      setHeight(sizeData.height);
    }
    if (sizeData?.width) {
      setWidth(sizeData.width);
    }
  }, [sizeData]);

  return (
    <Modal
      modalId={MODALS_IDS.AI_VIDEO_MODAL}
      stickToBottom={stickToBottom}
      mobileHeight="800px"
      panelId={PANEL_ID}
      modalHeight={height}
      modalWidth={width}
    >
      <LazyPanel
        panelDirectoryName="ai-video-panel"
        panelProps={{
          ...modalData.panelProps,
          panelId: PANEL_ID,
          isFullScreen: true,
          showBackButton: false,
          onClose,
        }}
      />
    </Modal>
  );
}

export const openAiVideoModal = (props: AiVideoModalProps): void => {
  window.PMW.redux.store.dispatch(
    openPanelInModal({
      modalId: MODALS_IDS.AI_VIDEO_MODAL,
      panelProps: props,
    })
  );
};
